import axios from 'axios';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import * as api from '../api/adapters/json_api';

// FIN-1281: supplier invoices read from the mailbox, waiting in their own queue until a
// person raises them into invoices. Read only, plus the four actions that move a row.
const type = 'api_resources/invoice_imports';
const typeRows = 'invoice_imports';

export const LIST_FIELDS = [
  'state', 'match_kind', 'match_notes', 'mailbox_label', 'received_at', 'sender_email', 'sender_name', 'subject',
  'attachment_filename', 'document_url', 'dry_run', 'failure_reason', 'number', 'total', 'currency', 'issued_at',
  'supplier_name', 'vessel_name', 'document_kind', 'confidence', 'supplier_id', 'purchase_order_id', 'purchase_order',
  'invoice_id', 'invoice', 'manual_invoice', 'actions', 'item_url', 'department',
  'order_ok', 'bank_ok', 'order_notes', 'bank_notes', 'replies', 'duplicate_of',
  'order_has_aae', 'approval_attached', // FIN-1339
].join(',');

// Newest first unless a column is chosen; the id breaks ties so a page never repeats a row.
export const sortOptions = (sort) =>
  (!sort || isEmpty(sort.field) ? '-received_at,-id' : `${sort.direction === 'asc' ? '' : '-'}${sort.field},-id`);

// Blank filters are not sent; the search goes as the `search` filter.
export const filterOptions = (filter = {}, search = '') => {
  const sent = {};
  Object.entries(filter).forEach(([field, value]) => {
    if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) return;
    sent[field] = value;
  });
  if (!isEmpty(search)) sent.search = search;
  return sent;
};

// FIN-1300: the companies picked in the header (a setting, as on the invoices page) narrow
// the list, the counts and the totals alike — unless the filter names its own.
export const withCompanies = (filter = {}, settings) => (
  filter.company_id ? filter : { ...filter, company_id: get(settings, 'global.importCompanies') || [] }
);

export default {
  index: (pagination, sort, search, filter, settings) => api.read({ type }, {
    query: {
      sort: sortOptions(sort),
      page: { size: pagination.itemsPerPage, number: pagination.activePage },
      filter: filterOptions(withCompanies(filter, settings), search),
      fields: { [typeRows]: LIST_FIELDS },
    },
  }),

  show: (id) => api.read({ type, id }, {}),

  // { awaiting, matched, partial, unmatched, awaiting_supplier } — for the badges, within the filters and the search.
  counts: (filter = {}, search = '') => api.read({ type, id: 'counts' }, { query: { filter: filterOptions(filter, search) } }).then((response) => response.data),

  // The one click on a full match; with an invoice id, links an invoice made by hand.
  raiseInvoice: (id, invoiceId) => api.create({ type, id, action: 'raise_invoice' }, {}, invoiceId ? { invoice_id: invoiceId } : {}),

  rematch: (id) => api.create({ type, id, action: 'rematch' }, {}, {}),

  reject: (id, reason) => api.create({ type, id, action: 'reject' }, {}, { reason }),

  // FIN-1322: the invoice number a document does not show; refused with the invoice that already has it.
  updateNumber: (id, number) => api.create({ type, id, action: 'update_number' }, {}, { number }),
  updateTotal: (id, total) => api.create({ type, id, action: 'update_total' }, {}, { total }), // FIN-1337

  // FIN-1329: an invoice or a credit note (kind: invoice | credit_note).
  updateKind: (id, kind) => api.create({ type, id, action: 'update_kind' }, {}, { kind }),

  // FIN-1323: the order's lines in place of the document's ({ mode: all|share|chosen, item_ids }), and back.
  takeOrderLines: (id, { mode, item_ids: itemIds }) => api.create({ type, id, action: 'take_order_lines' }, {}, { mode, item_ids: itemIds }),
  restoreDocumentLines: (id) => api.create({ type, id, action: 'restore_document_lines' }, {}, {}),

  // FIN-1316: what the import set aside goes back to the queue.
  restore: (id) => api.create({ type, id, action: 'restore' }, {}, {}),

  // FIN-1305: from the department's address, in the supplier's thread — the server checks the sender.
  requestSupplier: (id, { to, from, subject, body }) => api.create({ type, id, action: 'request_supplier' }, {}, { to, from, subject, body }),

  // FIN-1302: to the next department, with a word — and files (FIN-1339: signed ids of direct uploads).
  handOver: (id, { departmentId, comment, attachments }) => api.create({ type, id, action: 'hand_over' }, {}, { department_id: departmentId, comment, attachments }),

  // FIN-1340: the corrected invoice that came outside the mailbox (a signed id of a direct upload) in place of the document.
  replaceDocument: (id, { file, comment }) => api.create({ type, id, action: 'replace_document' }, {}, { file, comment }),

  // FIN-1339: the author takes a file off a log entry.
  deleteCheckAttachment: (id, fileId) => api.destroy({ type, id, action: `check_attachments/${fileId}` }),

  // FIN-1307: the vessel or company put right by hand; the match runs again.
  reassign: (id, { vesselId, companyId, comment }) => api.create({ type, id, action: 'reassign' }, {}, { vessel_id: vesselId, company_id: companyId, comment }),

  // FIN-1304: the order fits the document — to the department that raises the invoice.
  confirmOrder: (id, { departmentId, comment, attachments }) => api.create({ type, id, action: 'confirm_order' }, {}, { department_id: departmentId, comment, attachments }),

  // The orders a person may link the row to: without a search, the likely ones (the
  // document's supplier and vessel first); with one, any posted order it finds.
  orderOptions: (id, search) => api.read({ type, id, action: 'order_options' }, { query: search ? { search } : {} })
    .then((response) => response.data.orders),

  // Links the row to the order a person chose; null hands the choice back to the match.
  linkOrder: (id, purchaseOrderId) => api.create({ type, id, action: 'link_order' }, {}, { purchase_order_id: purchaseOrderId }),

  // FIN-1296: the document as BYTES, for Show PDF. A frame cannot show the API host's file
  // (X-Frame-Options: SAMEORIGIN), but a blob of the page's own origin it can; the API allows
  // the cross-origin read (Rack::Cors). The address is signed, so no auth headers — they would
  // only add a preflight.
  document: (href) => axios.get(href, { responseType: 'blob' }),

  // { waiting, due_soon, oldest_waiting_days, raised_this_month, dry_run } — the page totals, within the filters and the search.
  totals: (filter = {}, search = '') => api.read({ type, id: 'totals' }, { query: { filter: filterOptions(filter, search) } }).then((response) => response.data),
};
